import React, {PureComponent} from "react"
import {withTheme, WithDefaultTheme} from "styled-components"

import id from "@irati/utils/id"
import {isUserDOBRequired} from "@irati/deals/helpers/deals/redemptionEvents/dateOfBirth"
import {Deal} from "@irati/types"
import isBoostedPerk from "@irati/deals/helpers/deals/isBoostedPerk"
import {getTimeLimitedType} from "@irati/deals/helpers/deals/isTimeLimited"
import Flex from "@irati/ui/src/Flex"
import {H1, H3, H5, H6} from "@irati/ui/src/Typography"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"

import BoostedSaving from "../BoostedSaving"
import Saving from "../Saving"
import {H4Title, H5Title, ResponsiveSavingWrapper} from "./styled"
import DealHighlight, {DealHighlightRow} from "../DealHighlight"
import DealModes from "../../enums/DealModes"
import FlexiRewardPrice from "../Deal/FlexiRewardPrice"

interface Props extends WithDefaultTheme, WithPerkOrReward {
  deal: Deal,
  mode?: DealModes
}

class DealHeaderDetails extends PureComponent<Props> {
  static defaultProps: Pick<Props, "mode"> = {
    mode: DealModes.Default
  }

  render() {
    const {deal, mode, theme, isPerks, isRewards} = this.props

    const {deliveryHighlight} = deal
    const typeHighlight = getTimeLimitedType(deal)

    const isFullPage = mode === DealModes.FullPage
    const isCompact = mode === DealModes.Compact
    const isDefault = !isFullPage && !isCompact
    const dealRequiresUserDOB = isUserDOBRequired(deal)

    const idNamespace = id("deal-header-details", this.props)

    return (
      <div id={idNamespace}>
        {isFullPage && (
          <>
            {deal.brandName && <H3 color="contrast">{deal.brandName}</H3>}
            <H1 id={id("h2-title", idNamespace)} paddingBottom={2} overflowWrap="break-word">
              {deal.header}
            </H1>
          </>
        )}
        {isDefault && (
          <>
            {deal.brandName && <H5 color="contrast">{deal.brandName}</H5>}
            <H4Title id={id("h4-title", idNamespace)} paddingBottom={1.5} overflowWrap="break-word" marginTop={!deal.brandName && 3}>
              {deal.header}
            </H4Title>
          </>
        )}
        {isCompact && (
          <>
            {deal.brandName && <H6 color="contrast">{deal.brandName}</H6>}
            <H5Title id={id("h5-title", idNamespace)} paddingBottom={1.5} overflowWrap="break-word" marginTop={!deal.brandName && 3}>
              {deal.header}
            </H5Title>
          </>
        )}
        <Flex justifyContent={isCompact ? "center" : "space-between"} minHeight={theme.spacing.eight}>
          {!isCompact && (
            <DealHighlight>
              <DealHighlightRow id={id("type", idNamespace)} type={typeHighlight} size={20} />
              <DealHighlightRow id={id("delivery", idNamespace)} type={deliveryHighlight} size={20} />
            </DealHighlight>
          )}
          {isPerks && (
            <>
              {isBoostedPerk(deal) && <BoostedSaving isFullPage={isFullPage} deal={deal} isMobile={isFullPage} />}
              {!isBoostedPerk(deal) && (
                <ResponsiveSavingWrapper isCompact={isCompact} isFullPage={isFullPage}>
                  <Saving
                    id={idNamespace}
                    data-test="savings-value"
                    addSavingsTypeEmoji={dealRequiresUserDOB}
                    deal={deal}
                    smallSavingsHeader />
                </ResponsiveSavingWrapper>
              )}
            </>
          )}
          {isRewards && <FlexiRewardPrice deal={deal} />}
        </Flex>
      </div>
    )
  }
}

export default withTheme(withPerkOrReward(DealHeaderDetails))
